/**
 * A dropdown that renders inside the page instead of as an operating-system popup.
 *
 * A native `<select>` cannot be styled where it matters: the open list is drawn by the OS, so on a
 * dark theme it arrives as an unstyled grey or black slab with the wrong font, the wrong radius and
 * no relation to the surface it came from. Every other menu in this app is a Headless UI panel; the
 * one that appears most often was the one that looked foreign.
 *
 * Headless UI's Listbox gives the keyboard behaviour a native select has and would otherwise have to
 * be rebuilt: type-ahead, arrow keys, Home/End, Escape to close, focus return to the button, and
 * `aria-activedescendant` for screen readers. Rolling this by hand is where custom dropdowns become
 * inaccessible.
 *
 * **The list is portalled to `document.body` and positioned `fixed`.** The first version rendered it
 * `absolute` inside a `relative` wrapper, which is fine until the field sits in a scroll container —
 * and the properties dock is one. There the list was clipped by the container *and* long enough to
 * give that container a scrollbar, so choosing an action meant scrolling a panel inside a panel. A
 * portal escapes whatever `overflow`, `z-index` or `transform` an ancestor imposes.
 *
 * The cost of `fixed` is that it does not travel with its field, so the position is recomputed on
 * scroll and resize while the list is open — with `capture: true`, because the thing that scrolls is
 * an ancestor and scroll events do not bubble.
 *
 * Options may be **disabled with a reason** — used for workflow triggers nothing fires yet, where
 * hiding the option would be a mystery and offering it would be a trap.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Listbox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';

export interface SelectOption {
  value: string;
  label: string;
  /** Secondary line, e.g. how many flows are listening. */
  hint?: string;
  disabled?: boolean;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  /** Shown when nothing is selected. */
  placeholder?: string;
  /** Accessible name — required, because a select with no label is unusable by keyboard. */
  ariaLabel: string;
  disabled?: boolean;
  className?: string;
  /** Rendered before the label of the selected option, e.g. a colour swatch. */
  adornment?: ReactNode;
}

/** Enough room for a few rows; below this the list opens upwards instead. */
const MIN_ROOM_BELOW = 240;

export function Select({
  value,
  onChange,
  options,
  placeholder = 'Choose…',
  ariaLabel,
  disabled,
  className = '',
  adornment,
}: Props) {
  const selected = options.find((option) => option.value === value);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [box, setBox] = useState<{ top: number; left: number; width: number; up: boolean } | null>(
    null,
  );

  const place = useCallback(() => {
    const node = buttonRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    // Opens upwards when there is more room there. A list running off the bottom of a laptop screen
    // is the commonest reason people conclude a dropdown is broken.
    const below = window.innerHeight - rect.bottom;
    const up = below < MIN_ROOM_BELOW && rect.top > below;
    setBox({
      top: up ? rect.top - 4 : rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      up,
    });
  }, []);

  return (
    <Listbox value={value} onChange={onChange} disabled={disabled}>
      {({ open }) => (
        <div className={`relative ${className}`}>
          <Listbox.Button
            ref={buttonRef}
            aria-label={ariaLabel}
            onClick={place}
            className="input-base flex w-full items-center justify-between gap-2 text-left disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="flex min-w-0 items-center gap-2">
              {adornment}
              <span
                className={`truncate ${selected ? '' : 'text-surface-400 dark:text-surface-500'}`}
              >
                {selected?.label ?? placeholder}
              </span>
            </span>
            <ChevronUpDownIcon className="h-4 w-4 shrink-0 text-surface-400" aria-hidden="true" />
          </Listbox.Button>

          {/*
            Rendered directly rather than wrapped in `Transition as={Fragment}`.
            
            That wrapper CRASHED the page: Headless UI's `Transition` clones its single child and
            hands it a ref, and the child here was a component returning `createPortal(...)`, which
            has no element to attach one to. Clicking the field threw and React unmounted the whole
            tree — a blank screen. The animation was not worth a component that could take the app
            down, so the panel now just appears, and `open` alone controls mounting.
          */}
          {open && (
            <FloatingPanel onReposition={place}>
              <Listbox.Options
                static
                style={{
                  position: 'fixed',
                  top: box?.top ?? -9999,
                  left: box?.left ?? -9999,
                  width: box?.width,
                  // Anchored by its bottom edge when opening upwards, so its height does not have to
                  // be measured first.
                  transform: box?.up ? 'translateY(-100%)' : undefined,
                }}
                // z-50 clears the sticky editor toolbar (z-30) this may open over.
                className="z-50 max-h-60 animate-fade-in overflow-auto rounded-lg border border-surface-200 bg-white py-1 text-sm shadow-modal focus:outline-none dark:border-surface-700 dark:bg-surface-800"
              >
                {options.length === 0 && (
                  <li className="px-3 py-2 text-surface-500 dark:text-surface-400">
                    Nothing to choose from
                  </li>
                )}
                {options.map((option) => (
                  <Listbox.Option
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    className={({ active, disabled: isDisabled }) =>
                      `flex cursor-default items-start gap-2 px-3 py-2 ${
                        isDisabled
                          ? 'text-surface-400 dark:text-surface-500'
                          : active
                            ? 'bg-primary-50 text-primary-700 dark:bg-primary-500/10 dark:text-primary-300'
                            : 'text-surface-700 dark:text-surface-200'
                      }`
                    }
                  >
                    {({ selected: isSelected }) => (
                      <>
                        {/* The tick keeps its space when absent, so labels line up down the list. */}
                        <CheckIcon
                          className={`mt-0.5 h-4 w-4 shrink-0 ${isSelected ? '' : 'invisible'}`}
                          aria-hidden="true"
                        />
                        <span className="min-w-0">
                          <span className={`block ${isSelected ? 'font-medium' : ''}`}>
                            {option.label}
                          </span>
                          {option.hint && (
                            <span className="block text-xs text-surface-500 dark:text-surface-400">
                              {option.hint}
                            </span>
                          )}
                        </span>
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </FloatingPanel>
          )}
        </div>
      )}
    </Listbox>
  );
}

/**
 * Renders its child into `document.body` and keeps it glued to its field while mounted.
 *
 * Mounted only while the list is open, so the listeners exist exactly as long as they are needed
 * rather than one pair per select on the page. `capture: true` on scroll because the element that
 * scrolls is an ancestor, and scroll events do not bubble.
 */
function FloatingPanel({
  children,
  onReposition,
}: {
  children: ReactNode;
  onReposition: () => void;
}) {
  useEffect(() => {
    onReposition();
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [onReposition]);

  return createPortal(children, document.body);
}
